import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Badge, Button, Card, Divider, Group, NumberInput, Progress, Select, SimpleGrid, Stack, Table, Text, TextInput, Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { api, ApiClientError } from "../api/client";
import type { DbTableStats, EligibilityItem, FloorRow, StorageAction, StorageJob, StorageSettings } from "../api/storageSchemas";
import { STORAGE_ACTIONS } from "../api/storageSchemas";
import { ErrorState, LoadingState } from "../components/ui";

/**
 * Settings → Storage, command-center edition (DB_RETENTION_AND_TIERING_OPTIONS.md
 * §5). Same page as V3 with the PostgreSQL catalogue: ONE database, months
 * age out through export → verify → drop partition; nothing else is deleted
 * on a schedule.
 */
const ACTION_LABELS: Record<StorageAction, string> = {
  partition_maintenance: "Partition maintenance (create upcoming months, drop the empty legacy partition)",
  analytics_shard_export: "Archive a month (export → verify → drop its rows)",
  kronos_cache_clear: "Kronos cache clear (older than the data horizon)",
  sim_log_cleanup: "Sweep log cleanup",
  db_compaction: "Database compaction (VACUUM FULL of bloated, non-current relations)",
  restored_range_expiry: "Expire restored months",
  prune_inactive_configs: "Prune unreferenced config snapshots",
  decision_log_rewrite: "Rewrite legacy decision rows to the compact format",
};

const SETTING_FIELDS: Array<{ key: keyof StorageSettings; label: string; hint: string }> = [
  { key: "analytics_hot_months", label: "Hot window (months)", hint: "older months become archive candidates; never below the Kronos requirement" },
  { key: "kronos_horizon_days", label: "Kronos data horizon (days)", hint: "ceiling; also set on Settings → Kronos" },
  { key: "kronos_horizon_cap_days", label: "Kronos horizon hard cap (days)", hint: "" },
  { key: "restore_default_days", label: "Restore duration (days)", hint: "" },
  { key: "min_ready_bytes", label: "Minimum ready size before notifying (bytes)", hint: "avoids nagging for small amounts" },
  { key: "sim_logs_max_age_days", label: "Sweep logs max age (days)", hint: "" },
  { key: "sim_logs_max_bytes", label: "Sweep logs max total (bytes)", hint: "" },
  { key: "alert_free_disk_gb", label: "Alert: free disk below (GB)", hint: "" },
  { key: "notify_digest_hour_utc", label: "Daily digest hour (UTC)", hint: "" },
  { key: "ticks_floor_hours", label: "Raw ticks floor (hours)", hint: "V3 live-DB floor; informational here (one database)" },
  { key: "checks_floor_hours", label: "Decision log floor (hours)", hint: "V3 live-DB floor; informational here" },
  { key: "overrides_floor_days", label: "Manual overrides floor (days)", hint: "V3 live-DB floor; informational here" },
];

function gb(bytes: number | undefined | null): string {
  return bytes == null ? "—" : `${(bytes / 1e9).toFixed(2)} GB`;
}

function dur(seconds: number): string {
  if (seconds <= 0) return "—";
  if (seconds >= 86400) return `${(seconds / 86400).toFixed(seconds % 86400 ? 1 : 0)} d`;
  return `${(seconds / 3600).toFixed(seconds % 3600 ? 1 : 0)} h`;
}

function describeError(err: unknown): string {
  if (err instanceof ApiClientError) return err.message;
  return err instanceof Error ? err.message : String(err);
}

export function StorageSettingsPage() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ["storage", "status"], queryFn: () => api.storage.status(), refetchInterval: 5000 });
  const archive = useQuery({ queryKey: ["storage", "archive"], queryFn: () => api.storage.archive(), refetchInterval: 15000 });
  const jobs = useQuery({ queryKey: ["storage", "jobs"], queryFn: () => api.storage.jobs(), refetchInterval: 5000 });
  const [draft, setDraft] = useState<Partial<StorageSettings>>({});
  const [confirmText, setConfirmText] = useState<Record<string, string>>({});
  const [previews, setPreviews] = useState<Record<string, StorageJob>>({});
  const [monthPick, setMonthPick] = useState<string | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["storage"] });
  const toastError = (err: unknown) => notifications.show({ color: "red", title: "Storage", message: describeError(err) });

  const save = useMutation({
    mutationFn: () => api.storage.updateSettings(draft),
    onSuccess: () => { setDraft({}); invalidate(); notifications.show({ color: "green", title: "Storage", message: "Settings saved" }); },
    onError: toastError,
  });
  const scan = useMutation({ mutationFn: () => api.storage.scan(), onSuccess: invalidate, onError: toastError });
  const setMode = useMutation({
    mutationFn: (v: { action: StorageAction; mode: "notify_only" | "auto" }) => api.storage.setMode(v.action, v.mode),
    onSuccess: invalidate, onError: toastError,
  });
  const preview = useMutation({
    mutationFn: (v: { action: StorageAction; params: Record<string, unknown> }) => api.storage.preview(v.action, v.params),
    onSuccess: (job, v) => setPreviews((p) => ({ ...p, [v.action]: job })),
    onError: toastError,
  });
  const run = useMutation({
    mutationFn: (v: { action: StorageAction; params: Record<string, unknown> }) => api.storage.run(v.action, v.params),
    onSuccess: (job) => {
      invalidate();
      notifications.show({ color: job.status === "succeeded" ? "green" : "yellow", title: ACTION_LABELS[job.action as StorageAction] ?? job.action,
        message: `${job.status}${job.message ? ` — ${job.message}` : ""}` });
    },
    onError: toastError,
  });
  const cancel = useMutation({ mutationFn: () => api.storage.cancel(), onSuccess: invalidate, onError: toastError });
  const restore = useMutation({ mutationFn: (segmentId: string) => api.storage.restore(segmentId), onSuccess: invalidate, onError: toastError });
  const extend = useMutation({ mutationFn: (segmentId: string) => api.storage.extend(segmentId, 14), onSuccess: invalidate, onError: toastError });

  const s = status.data;
  const settings = useMemo(() => ({ ...(s?.settings ?? {}), ...draft }) as StorageSettings, [s, draft]);
  if (status.isLoading) return <LoadingState label="Loading storage status…" />;
  if (status.isError || !s) return <ErrorState error={status.error as Error} onRetry={() => void status.refetch()} />;

  const eligibilityByAction = new Map(s.eligibility.map((e) => [e.action, e] as [string, EligibilityItem]));
  const coldMonths = ((eligibilityByAction.get("analytics_shard_export")?.details.months ?? []) as Array<{ month: string; ticks: number; checks: number; in_legacy: boolean }>);
  const tables = (s.live_db.tables ?? []) as DbTableStats[];

  const paramsFor = (action: StorageAction): Record<string, unknown> => {
    if (action !== "analytics_shard_export") return {};
    const pick = monthPick ?? coldMonths[0]?.month ?? null;
    return pick ? { month: pick } : {};
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>Storage</Title>
          <Text c="dimmed" size="sm">
            One PostgreSQL holds live and historical data; months older than the hot window are archived (export → verify → drop
            partition) only when you run it. Partition maintenance is the only automatic action and never deletes data. Eligible work
            is announced through your notification rules (<code>storage_alert</code>) and here. Last scan: {s.last_scan_at ?? "never"}.
          </Text>
        </div>
        <Group>
          <Button variant="light" onClick={() => scan.mutate()} loading={scan.isPending}>Scan now</Button>
          <Button onClick={() => save.mutate()} disabled={Object.keys(draft).length === 0} loading={save.isPending}>Save settings</Button>
        </Group>
      </Group>

      {s.deletion_blocked_reason && (
        <Alert color="orange" title="Archiving paused">{s.deletion_blocked_reason}</Alert>
      )}
      {s.alerts.filter((a) => a.pending).length > 0 && (
        <Alert color="blue" title={`${s.pending_alerts} pending storage alert(s)`}>
          <Stack gap={2}>
            {s.alerts.filter((a) => a.pending).map((a) => (
              <Text key={a.key} size="sm"><b>{a.title}</b>: {a.message}</Text>
            ))}
          </Stack>
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, md: 3 }}>
        <Card withBorder>
          <Text size="sm" c="dimmed">Database</Text>
          <Text fw={600}>{gb(s.live_db.bytes)}</Text>
          <Text size="xs" c="dimmed">{tables.length > 0 ? `${tables.length} heavy relations tracked` : ""}</Text>
        </Card>
        <Card withBorder>
          <Text size="sm" c="dimmed">Free disk</Text>
          <Text fw={600}>{gb(s.disk_free_bytes)}</Text>
          <Text size="xs" c="dimmed">alert below {settings.alert_free_disk_gb} GB</Text>
        </Card>
        <Card withBorder>
          <Text size="sm" c="dimmed">Archive</Text>
          <Text fw={600} size="sm" style={{ wordBreak: "break-all" }}>{s.archive_dir}</Text>
          <Text size="xs" c="dimmed">{(archive.data?.items ?? []).length} month(s) archived</Text>
        </Card>
      </SimpleGrid>

      <Card withBorder>
        <Title order={4}>Kronos requirement per market</Title>
        <Table striped highlightOnHover mt="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Market</Table.Th><Table.Th>Kronos needs</Table.Th><Table.Th>Horizon</Table.Th>
              <Table.Th>Held ticks from</Table.Th><Table.Th>Held candles from</Table.Th><Table.Th>Held cache from</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {s.floors.map((f: FloorRow) => (
              <Table.Tr key={f.market_id}>
                <Table.Td>{f.market_id}</Table.Td>
                <Table.Td>
                  {f.requirement_seconds > 0 ? dur(f.requirement_seconds) : "off"}{" "}
                  {!f.within_ceiling && <Badge color="red" size="xs">exceeds horizon</Badge>}
                </Table.Td>
                <Table.Td>{dur(f.ceiling_seconds)}</Table.Td>
                <Table.Td>{f.held_ticks_from ?? "—"}</Table.Td>
                <Table.Td>{f.held_candles_from ?? "—"}</Table.Td>
                <Table.Td>{f.held_kronos_from ?? "—"}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>

      <Card withBorder>
        <Title order={4}>Settings</Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} mt="sm">
          {SETTING_FIELDS.map((f) => (
            <NumberInput
              key={f.key}
              label={f.label}
              description={f.hint || undefined}
              value={settings[f.key] as number}
              min={0}
              onChange={(v) => setDraft((d) => ({ ...d, [f.key]: Number(v) }))}
            />
          ))}
        </SimpleGrid>
      </Card>

      <Card withBorder>
        <Group justify="space-between">
          <Title order={4}>Actions</Title>
          {s.current_job && (
            <Group>
              <Text size="sm">{ACTION_LABELS[s.current_job.action as StorageAction] ?? s.current_job.action}: {s.current_job.message ?? "running"}</Text>
              <Progress value={Math.round((s.current_job.progress ?? 0) * 100)} w={160} />
              <Button size="xs" color="red" variant="light" onClick={() => cancel.mutate()}>Cancel</Button>
            </Group>
          )}
        </Group>
        <Stack gap="sm" mt="sm">
          {STORAGE_ACTIONS.map((action) => {
            const e = eligibilityByAction.get(action);
            const p = previews[action];
            const mode = s.action_modes[action] ?? "notify_only";
            return (
              <Card key={action} withBorder padding="sm">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <div style={{ flex: 1 }}>
                    <Group gap="xs">
                      <Text fw={600}>{ACTION_LABELS[action]}</Text>
                      {e?.eligible ? <Badge color="blue" size="xs">ready</Badge> : <Badge color="gray" size="xs">nothing to do</Badge>}
                    </Group>
                    <Text size="sm" c="dimmed">{e?.summary ?? "—"}{e && e.ready_bytes_estimate > 0 ? ` · ~${gb(e.ready_bytes_estimate)}` : ""}</Text>
                    {action === "analytics_shard_export" && coldMonths.length > 0 && (
                      <Select
                        mt="xs" size="xs" w={320}
                        data={coldMonths.map((m) => ({ value: m.month, label: `${m.month} · ${m.ticks.toLocaleString()} ticks · ${m.checks.toLocaleString()} decisions${m.in_legacy ? " · legacy partition (batched delete)" : " · drop partition"}` }))}
                        value={monthPick ?? coldMonths[0]!.month}
                        onChange={setMonthPick}
                      />
                    )}
                    {p && (
                      <Text size="xs" mt="xs" style={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
                        preview: {JSON.stringify({ rows: p.result?.rows_deleted ?? p.result?.rows_copied, bytes: p.result?.bytes_freed_estimate, details: p.result?.details }, null, 0).slice(0, 600)}
                      </Text>
                    )}
                  </div>
                  <Stack gap={6} align="flex-end">
                    <Select
                      size="xs" w={140}
                      data={[{ value: "notify_only", label: "Notify only" }, { value: "auto", label: "Automatic" }]}
                      value={mode}
                      onChange={(v) => v && setMode.mutate({ action, mode: v as "notify_only" | "auto" })}
                    />
                    <Group gap={6}>
                      <Button size="xs" variant="light" onClick={() => preview.mutate({ action, params: paramsFor(action) })} loading={preview.isPending && preview.variables?.action === action}>Preview</Button>
                      <TextInput size="xs" w={190} placeholder={`type ${action}`} value={confirmText[action] ?? ""} onChange={(ev) => setConfirmText((c) => ({ ...c, [action]: ev.currentTarget.value }))} />
                      <Button size="xs" color="red" disabled={confirmText[action] !== action || !!s.current_job} onClick={() => run.mutate({ action, params: paramsFor(action) })}>Run</Button>
                    </Group>
                  </Stack>
                </Group>
              </Card>
            );
          })}
        </Stack>
      </Card>

      <Card withBorder>
        <Title order={4}>Archived months</Title>
        <Text size="sm" c="dimmed">Restore copies a month back into the database for {settings.restore_default_days} days (its partition is re-created); every page and simulator sees it unchanged.</Text>
        <Table mt="sm" striped>
          <Table.Thead>
            <Table.Tr><Table.Th>Month</Table.Th><Table.Th>Status</Table.Th><Table.Th>Restored until</Table.Th><Table.Th>Location</Table.Th><Table.Th /></Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(archive.data?.items ?? []).map((seg) => (
              <Table.Tr key={seg.segment_id}>
                <Table.Td>{seg.month}</Table.Td>
                <Table.Td><Badge color={seg.status === "restored" ? "green" : "gray"} size="xs">{seg.status}</Badge></Table.Td>
                <Table.Td>{seg.restored_until ?? "—"}</Table.Td>
                <Table.Td><Text size="xs" style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{seg.path}</Text></Table.Td>
                <Table.Td>
                  {seg.status === "exported"
                    ? <Button size="xs" variant="light" onClick={() => restore.mutate(seg.segment_id)}>Restore</Button>
                    : <Button size="xs" variant="light" onClick={() => extend.mutate(seg.segment_id)}>Extend 14 d</Button>}
                </Table.Td>
              </Table.Tr>
            ))}
            {(archive.data?.items ?? []).length === 0 && (
              <Table.Tr><Table.Td colSpan={5}><Text size="sm" c="dimmed">No months archived yet.</Text></Table.Td></Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      {tables.length > 0 && (
        <Card withBorder>
          <Title order={4}>Heavy relations</Title>
          <Table mt="sm" striped>
            <Table.Thead>
              <Table.Tr><Table.Th>Relation</Table.Th><Table.Th>Size</Table.Th><Table.Th>Live rows</Table.Th><Table.Th>Dead rows</Table.Th><Table.Th>Bloat</Table.Th></Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tables.slice(0, 20).map((t) => (
                <Table.Tr key={t.name}>
                  <Table.Td><Text size="xs" style={{ fontFamily: "monospace" }}>{t.name}</Text></Table.Td>
                  <Table.Td>{gb(t.bytes)}</Table.Td>
                  <Table.Td>{t.live_rows.toLocaleString()}</Table.Td>
                  <Table.Td>{t.dead_rows.toLocaleString()}</Table.Td>
                  <Table.Td>{`${(t.dead_ratio * 100).toFixed(0)} %`}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      <Card withBorder>
        <Title order={4}>Recent jobs</Title>
        <Divider my="xs" />
        <Table striped>
          <Table.Thead>
            <Table.Tr><Table.Th>When</Table.Th><Table.Th>Action</Table.Th><Table.Th>Mode</Table.Th><Table.Th>Status</Table.Th><Table.Th>Result</Table.Th></Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(jobs.data?.items ?? []).slice(0, 25).map((j) => (
              <Table.Tr key={j.job_id}>
                <Table.Td>{j.created_at}</Table.Td>
                <Table.Td>{j.action}</Table.Td>
                <Table.Td>{j.dry_run ? "preview" : "run"}</Table.Td>
                <Table.Td><Badge size="xs" color={j.status === "succeeded" ? "green" : j.status === "failed" ? "red" : "gray"}>{j.status}</Badge></Table.Td>
                <Table.Td>
                  <Text size="xs">
                    {j.result ? `deleted ${String(j.result.rows_deleted ?? 0)} · copied ${String(j.result.rows_copied ?? 0)}` : (j.message ?? "")}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
